import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { ComponentType } from 'react';

import {
  type CompareDeltaVisual,
  formatEntityTitleForDisplay,
} from 'src/components/Retention/formatters';
import { retentionHelpTooltipComponentsProps } from 'src/components/Retention/helpTooltipProps';
import palette from 'src/theme/palette';

/** Semantic KPI icon in the top-right tile (reference dashboard). */
export type MetricCardKpiIcon =
  | 'calendar'
  | 'clock'
  | 'bag'
  | 'person'
  | 'money'
  | 'trend';

const KPI_ICONS: Record<MetricCardKpiIcon, ComponentType<SvgIconProps>> = {
  calendar: EventAvailableRoundedIcon,
  clock: AccessTimeRoundedIcon,
  bag: ShoppingBagOutlinedIcon,
  person: PersonOutlineRoundedIcon,
  money: MonetizationOnRoundedIcon,
  trend: ShowChartRoundedIcon,
};

const deltaPositive = '#16a34a';
const deltaNegative = '#dc2626';

interface MetricCardProps {
  label: string;
  tooltip: string;
  value: string;
  caption?: string;
  /** Confronto stile dashboard: freccia + % + range compare. */
  compareDelta?: CompareDeltaVisual | null;
  accent?: string;
  /** Semantic icon shown in the left chip for KPI identity. */
  kpiIcon?: MetricCardKpiIcon;
  /**
   * `entity`: titoli catalogo — title case se tutto maiuscolo + tooltip col nome originale.
   * Le dimensioni tipografiche sono le stesse per tutte le card (layout unificato).
   */
  valueMode?: 'metric' | 'entity';
}

export function MetricCard({
  label,
  tooltip,
  value,
  caption,
  compareDelta,
  accent = palette.primary.main,
  kpiIcon = 'calendar',
  valueMode = 'metric',
}: MetricCardProps) {
  const IconComponent = KPI_ICONS[kpiIcon];
  const displayValue =
    valueMode === 'entity' ? formatEntityTitleForDisplay(value) : value;
  const showEntityCatalogTooltip =
    valueMode === 'entity' && value !== 'Not enough data' && value.length > 0;
  const deltaColor = compareDelta
    ? compareDelta.direction === 'neutral'
      ? palette.text.secondary
      : compareDelta.good
        ? deltaPositive
        : deltaNegative
    : palette.text.secondary;

  const primaryValueTypography = (
    <Typography
      sx={{
        mt: 0.35,
        color: palette.text.primary,
        fontWeight: 700,
        fontSize: { xs: '0.95rem', sm: '1rem', lg: '1.05rem' },
        letterSpacing: '0.01em',
        lineHeight: 1.38,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        cursor: showEntityCatalogTooltip ? 'help' : 'inherit',
      }}
    >
      {displayValue}
    </Typography>
  );

  const primaryValueBlock = showEntityCatalogTooltip ? (
    <Tooltip
      title={<Box sx={{ maxWidth: 300 }}>{value}</Box>}
      arrow
      placement="top"
      enterTouchDelay={0}
      componentsProps={retentionHelpTooltipComponentsProps}
    >
      {primaryValueTypography}
    </Tooltip>
  ) : (
    primaryValueTypography
  );

  return (
    <Card
      data-retention-pdf-block="metric-card"
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.08)',
        boxShadow: '0 8px 24px -22px rgba(15, 23, 42, 0.28)',
        backgroundColor: palette.common.white,
      }}
    >
      <CardContent sx={{ p: 1.2, pt: 1.2, '&:last-child': { pb: 1.1 } }}>
        <Box
          display="flex"
          alignItems="flex-start"
          gap={1.5}
          sx={{ minHeight: 0 }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `${accent}18`,
              color: accent,
              flexShrink: 0,
              border: '1px solid',
              borderColor: `${accent}38`,
            }}
          >
            <IconComponent sx={{ fontSize: 22, color: accent }} />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1, py: 0.1 }}>
            <Box display="flex" alignItems="center" gap={0.65}>
              <Typography
                variant="caption"
                sx={{
                  color: palette.text.secondary,
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                  lineHeight: 1.25,
                  fontSize: '0.6875rem',
                  textTransform: 'none',
                }}
              >
                {label}
              </Typography>
              <Tooltip
                title={tooltip}
                arrow
                placement="top"
                enterTouchDelay={0}
                componentsProps={retentionHelpTooltipComponentsProps}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 14,
                    color: palette.text.secondary,
                    mt: '-1px',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            </Box>

            {primaryValueBlock}

            {caption && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.35,
                  color: palette.text.secondary,
                  lineHeight: 1.35,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {caption}
              </Typography>
            )}

            {compareDelta && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.35,
                  mt: 0.4,
                  rowGap: 0.15,
                }}
              >
                {compareDelta.direction === 'down' && (
                  <ArrowDownwardRoundedIcon
                    sx={{ fontSize: 17, color: deltaColor, mt: '1px' }}
                  />
                )}
                {compareDelta.direction === 'up' && (
                  <ArrowUpwardRoundedIcon
                    sx={{ fontSize: 17, color: deltaColor, mt: '1px' }}
                  />
                )}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 800,
                    color: deltaColor,
                    lineHeight: 1.2,
                  }}
                >
                  {compareDelta.pctLabel}
                </Typography>
                {compareDelta.vsLabel ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: palette.text.secondary,
                      lineHeight: 1.2,
                    }}
                  >
                    {compareDelta.vsLabel}
                  </Typography>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default MetricCard;
